'use client';

import Image, { type StaticImageData } from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { type MouseEvent, useState } from 'react';
import Button from './Button';
import SvgArrowDown from './svg/SvgArrowDown';

type AccordionItem = {
    title: string;
    body: string;
    image: StaticImageData;
    cta?: string;
};

export default function Accordion({ items }: { items: AccordionItem[] }) {
    const locale = useLocale();
    const t = useTranslations('jobOffers');

    const [activeItem, setActiveItem] = useState<number | null>(null);

    const handleClick = (e: MouseEvent<HTMLElement>, key: number) => {
        if (key === activeItem) {
            setActiveItem(null);
        } else {
            setActiveItem(key);

            const target = e.currentTarget;
            setTimeout(() => {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
        }
    };

    return (
        <div className="flex flex-col gap-2.5">
            {items.map(({ title, body, image, cta }, key) => (
                <article className="bg-white rounded-[35px]" key={title}>
                    <div className="w-full bg-linear-to-tr from-accent-color to-accent-color/50 text-white rounded-[35px]">
                        <h3>
                            <button
                                type="button"
                                className="w-full flex items-center justify-between gap-4 p-6 md:py-6 md:px-8 cursor-pointer text-left text-md md:text-lg font-semibold"
                                onClick={(e) => handleClick(e, key)}
                                aria-expanded={key === activeItem}
                                aria-controls={`job-panel-${key}`}
                            >
                                <span>{title}</span>
                                <span
                                    className="w-14 h-14 bg-white/25 shrink-0 rounded-full inline-flex items-center justify-center"
                                    aria-hidden="true"
                                >
                                    <SvgArrowDown
                                        width={18}
                                        className={`transform transition-transform duration-500 ${
                                            key === activeItem ? 'rotate-180' : ''
                                        }`}
                                    />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={`job-panel-${key}`}
                            className={`accordion ${
                                key === activeItem ? 'accordion-open' : ''
                            }`}
                        >
                            <div className="px-6 pb-6 md:py-6 md:px-8 flex flex-col md:flex-row gap-6 md:gap-12 items-start">
                                <div className="w-full md:max-w-[300px] aspect-[6/5] md:aspect-[5/6] h-full flex relative">
                                    <Image
                                        src={image}
                                        placeholder="blur"
                                        sizes="(min-width: 768px) 300px, 100vw"
                                        className="size-full object-cover object-top rounded-[45px]"
                                        alt=""
                                    />
                                </div>
                                <div className="flex-1 flex flex-col gap-8">
                                    <div
                                        className="flex flex-col gap-8 max-w-[430px] leading-[1.6]"
                                        dangerouslySetInnerHTML={{ __html: body }}
                                    />
                                    {locale !== 'en' && (
                                        <div className="text-center md:text-left">
                                            <Button
                                                href={`/register?note=${encodeURIComponent(
                                                    `${t('common.jobOffer')}: ${title}`
                                                )}`}
                                                className="w-max max-w-max"
                                            >
                                                {cta || t('common.cta')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </article>
            ))}
        </div>
    );
}
